import React, {
  useContext,
  useEffect,
  useState,
  forwardRef,
  type SyntheticEvent,
  type ReactNode,
  type ComponentPropsWithRef,
  type MouseEventHandler,
  useRef,
} from 'react';

import clsx from 'clsx';
import styled from '@emotion/styled';
import { theme, variant } from '@lucid/theme';
import { useSiteMetadata, usePathPrefix } from '@lucid/hooks';
import {
  isAnchorPath,
  getInternalLink,
  sendBeaconEvent,
  withBeaconURLParams,
  withCookieURLParams,
  isBrowser,
  assignRefs,
  parseMetatag,
  withURLParams,
} from '@lucid/utils';
import { navigate, withPrefix } from 'gatsby';
import gsap from 'gsap';

import type { Lucid } from '../../types/types';
import decorate from '../decorate/decorate';
import Button, { type ButtonTypes, type ButtonOptions } from '../button/button';
import Icon, { type IconTypes } from '../icon/icon';
import StyledGatsbyLink from './styled-gatsby-link';
import LayoutContext from '../context/layout-context';

export type LinkUnderlineType = 'none' | 'hover' | 'always';

export type IconAnimationType =
  | 'default'
  | 'navigational'
  | 'reverseNavigational'
  | 'expand'
  | 'collapse'
  | 'jumpLink'
  | 'jumpLinkInverse';

export interface BaseLinkOptions {
  /** The children for the Link component.  */
  children?: ReactNode;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Sets icon & animation based on the link functionality
   ** default: Link outside of the Arrow indicator system (ex: nav, footer, wrapper for Card, etc.)
   ** navigational: Leads to another page (internal or external)
   ** reverseNavigational: Internal Only - use of arrow before text as 'back button'
   ** expand: Exposes hidden information by expanding a section
   ** collapse: Restores expanded section
   ** jumpLink: Carries user from one section to another (direction: down)
   ** jumpLinkInverse: Carries user from one section to another (direction: up)
   */
  iconAnimation?: IconAnimationType;
  /** Determines if the link is active. */
  isActive?: boolean;
  /** Checks if the link should be disabled. */
  isDisabled?: boolean;
  /** Sets the underline value for the Link. */
  underline?: LinkUnderlineType;
}

export interface LinkOptions {
  /** The link used in the Link component. */
  href?: string;
  /** The label used for the link or button, can also be passed
   * into the component as `children`
   */
  label?: string;
  /** The link used in the Link component. */
  to?: string;
}

type LinkElementHTMLProps = Omit<ComponentPropsWithRef<'a'>, 'as' | 'size' | 'type'>;

export interface LinkProps extends LinkOptions, BaseLinkOptions, LinkElementHTMLProps, ButtonOptions {
  /** Render a `button` instead of `a` link tag. */
  asButton?: boolean;
  /** The beacon event config. */
  beaconEvent?: Lucid.Beacon.Event | null;
  /** Extends a list of url paths that should not be considered as internal links. */
  blocklist?: string[];
  /** Data attribute specifying whether the link is being displayed in the style of a button.  */
  'data-link-as-button'?: boolean;
  /** Will set link `rel="noopener noreferrer"` and `target="_blank"` */
  externalAlwaysNewTab?: boolean;
  /** Will create an HTML `a` tag, instead of a GatsbyLink */
  excludeFromGatsby?: boolean | null;
  /** Add the name HTML attribute to the Link component. */
  name?: string;
  /** Will open the link in a new tab, by setting
   * `target="_blank"`
   */
  newTab?: boolean;
  /** Checks if the link should be prefixed */
  noPrefix?: boolean;
  /** Specifies the relationship between
   * the current document and the linked document/resource.
   */
  rel?: string;
  /** Outlines the behavior for scrolling to same-page anchors */
  scrollToOptions?: ScrollIntoViewOptions;
  /** Will check session storage and route to previous location  */
  shouldCheckHistory?: boolean;
  /** Allows for tabbing on keyboard to change links. */
  tabIndex?: number;
  /** Sets the link target */
  target?: string;
  /** Adds beacon params for cookie sync regardless of url. */
  willAddBeaconParams?: boolean;
  /** Adds passed in cookies to the link url params. */
  withCookieParams?: string[];
  /** The available variants for the Link component. */
  variant?: Lucid.Theme.Component.Link['Variant'] | Lucid.Theme.Component.Button['Variant'];
}

const StyledLink = styled.a<LinkProps>(
  variant({
    scale: 'link',
    variants: {
      defaultVariant: {},
    },
  }),
  ({ underline, isDisabled }) => [
    {
      label: 'link',
    },
    isDisabled && {
      opacity: 0.5,
      pointerEvents: 'none',
      cursor: 'default',
    },
    underline === 'always' && {
      textDecoration: 'underline',
    },
    underline === 'always' ||
      (underline === 'hover' && {
        '&:hover, &:active, &:focus': {
          textDecoration: 'underline',
        },
      }),
  ],
);

const LinkComponent = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      asButton = false,
      beaconEvent,
      blocklist = [],
      className,
      displayAsButton = false,
      externalAlwaysNewTab = false,
      excludeFromGatsby = false,
      iconAnimation = 'default',
      children,
      href,
      isDisabled = false,
      label,
      newTab = false,
      noPrefix = false,
      rel,
      scrollToOptions,
      shouldCheckHistory = false,
      target,
      onClick,
      to,
      type,
      underline = 'none',
      willAddBeaconParams = false,
      withCookieParams = [],
      variant = 'primary',
      ...rest
    },
    forwardedRef,
  ) => {
    const pathPrefix = usePathPrefix();
    const { product, siteDomain } = useSiteMetadata();
    const baseLink = to || href;
    const { isInternal, url } = getInternalLink({
      link: baseLink,
      pathPrefix,
      siteDomain,
      blocklist,
    });
    const [hrefTo, setHrefTo] = useState(url);
    const animationRef = useRef<HTMLAnchorElement>(null);
    const q = gsap.utils.selector(animationRef);
    const [hasHover, setHasHover] = useState(false);
    const { metatags } = useContext(LayoutContext);
    const parsedMetatags = parseMetatag(metatags, 'pragma');
    const iconTypes: Record<string, IconTypes | null> = {
      navigational: 'ArrowRightThin',
      reverseNavigational: 'ArrowRightThin',
      expand: 'CarotDown',
      collapse: 'CarotDown',
      jumpLink: 'ArrowRightThin',
      jumpLinkInverse: 'ArrowRightThin',
    };
    const hasIcon = Object.keys(iconTypes).includes(iconAnimation);
    const iconColor = ['primaryInverse', 'secondaryInverse'].includes(variant)
      ? 'white'
      : ['primary', 'secondary'].includes(variant) && ['expand', 'collapse'].includes(iconAnimation)
        ? undefined
        : 'coolGrey.100';

    useEffect(() => {
      if (!displayAsButton && animationRef.current && q('span').length) {
        hasHover
          ? gsap
              .timeline({ repeat: -1, repeatDelay: 0.7 })
              .to(q('span'), {
                x: iconAnimation === 'navigational' ? '0.6em' : iconAnimation === 'reverseNavigational' ? '-0.6em' : '',
                y: ['jumpLink', 'expand'].includes(iconAnimation)
                  ? '0.6em'
                  : ['collapse', 'jumplinkInverse'].includes(iconAnimation)
                    ? '-0.6em'
                    : '',
                duration: 0.5,
                ease: theme.beziers.slowStartEase,
                overwrite: true,
              })
              .to(q('span'), {
                x: '0em',
                y: '0em',
                duration: 0.5,
                ease: theme.beziers.fastEndEase,
              })
          : gsap.to(q('span'), {
              x: '0em',
              y: '0em',
              duration: 0.5,
              ease: theme.beziers.fastEndEase,
              overwrite: true,
            });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasHover, hasIcon, iconAnimation]);

    useEffect(() => {
      const hrefToLink = withBeaconURLParams(url, willAddBeaconParams);
      const hrefToLinkWithCookieParams = withCookieURLParams(hrefToLink, withCookieParams);
      const hrefToLinkWithCustomURLParams = withURLParams(hrefToLinkWithCookieParams, parsedMetatags);

      return setHrefTo(hrefToLinkWithCustomURLParams || '');
    }, [url, hrefTo, product, withCookieParams, willAddBeaconParams, parsedMetatags]);

    try {
      if (asButton && !onClick && !type) {
        throw `You must provide either an 'onClick' function
        or a 'type' when Link prop 'asButton' is true for ${hrefTo}.`;
      }

      if (displayAsButton && !hrefTo && !onClick) {
        throw `You must provide either a 'href', 'to', or 'onClick' function
        when Link prop 'displayAsButton' is true.`;
      }

      if (!isInternal && iconAnimation === 'reverseNavigational') {
        throw 'reverseNavigational animation should only be used on internal links';
      }
    } catch (error) {
      throw new Error(`${error}`);
    }

    const linkLabel = label ? label : children;
    let updatedLabels: string[] | undefined;
    // Splits last word off label to pair in <span> with Arrow icon
    // to ensure icon is always accompanied by text when overflowing to new line
    if (linkLabel && typeof linkLabel === 'string' && hasIcon) {
      const splitLabel = linkLabel.trim().split(' ');
      const lastWord = splitLabel.pop() || '';
      updatedLabels = [splitLabel.join(' '), lastWord];
    }
    const hasNoFollow = hrefTo && !isInternal && hrefTo.includes('lucid.app');
    const newTabProps =
      newTab || externalAlwaysNewTab
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : target
          ? { target }
          : undefined;

    const linkRelProps = rel
      ? newTabProps?.rel
        ? `${newTabProps?.rel} ${rel}`.trim()
        : rel.trim()
      : newTabProps?.rel
        ? newTabProps?.rel.trim()
        : undefined;

    const relProps = hasNoFollow
      ? `nofollow ${linkRelProps ? linkRelProps : ''}`.trim()
      : linkRelProps
        ? linkRelProps
        : undefined;

    const targetProps = target || newTabProps?.target ? newTabProps?.target : undefined;

    // Utillize the window 'back' iconAnimation, which will keep previous query
    // params, rather than navigate back to the previous page loaded new
    // without query params.
    const beforeNavigation = (e: SyntheticEvent<Element, Event>) => {
      if (shouldCheckHistory) {
        const prevLocation = sessionStorage.getItem('prevLocation');
        const baseTo = hrefTo && hrefTo.split('?')[0];

        if (prevLocation && baseTo?.startsWith(prevLocation)) {
          e.preventDefault();
          window.history.back();
        }
      }
    };

    const handleOnClick: MouseEventHandler<HTMLAnchorElement> = e => {
      if (beaconEvent) {
        sendBeaconEvent(beaconEvent);
      }

      if (['jumpLink', 'jumpLinkInverse', 'expand', 'collapse'].includes(iconAnimation)) {
        setHasHover(false);
      }

      beforeNavigation(e);

      if (onClick) {
        onClick(e);
      }

      if (hrefTo && isAnchorPath(hrefTo) && !onClick) {
        e.preventDefault();
        if (!scrollToOptions) {
          navigate(hrefTo);
        }
        if (isBrowser()) {
          const el = document.getElementById(hrefTo.slice(1));
          if (el) {
            el.scrollIntoView(scrollToOptions);
            // move keyboard focus for screenreaders
            el.tabIndex = -1;
            el.focus({ preventScroll: true });
          }
        }
      }
    };

    const currentHrefToValue = isInternal && !noPrefix && hrefTo ? withPrefix(hrefTo) : hrefTo;

    if (displayAsButton || asButton) {
      const buttonTag = asButton ? 'button' : Link;
      const buttonSize = rest.size as unknown as Lucid.Theme.Component.ButtonSize['Variant'];
      const buttonProps = { ...rest } as unknown as ButtonOptions;
      return (
        <Button
          {...buttonProps}
          data-link-as-button
          aria-disabled={isDisabled || rest['aria-disabled']}
          as={buttonTag}
          className={className}
          displayAsButton={displayAsButton}
          excludeFromGatsby={excludeFromGatsby}
          href={currentHrefToValue}
          iconAnimation={iconAnimation}
          // @ts-ignore FIXME: handle correct type change for button
          onClick={handleOnClick}
          size={buttonSize}
          target={targetProps}
          to={currentHrefToValue}
          type={type as ButtonTypes}
          variant={variant as Lucid.Theme.Component.Button['Variant']}
        >
          {linkLabel}
        </Button>
      );
    }

    if (isInternal && !noPrefix && !newTab && !excludeFromGatsby) {
      return (
        <StyledGatsbyLink
          {...rest}
          aria-disabled={isDisabled || rest['aria-disabled']}
          className={className}
          data-link-as-button={rest['data-link-as-button'] || (!asButton && displayAsButton) ? true : undefined}
          data-link-as-link={!rest['data-link-as-button'] && !asButton && !displayAsButton ? true : undefined}
          hasIcon={hasIcon}
          iconAnimation={iconAnimation}
          iconColor={iconColor}
          iconType={iconTypes[iconAnimation]!}
          onClick={handleOnClick}
          rel={relProps}
          target={targetProps}
          to={hrefTo || ''}
          underline={underline}
          updatedLabels={updatedLabels}
          variant={variant as Lucid.Theme.Component.Link['Variant']}
        >
          {linkLabel}
        </StyledGatsbyLink>
      );
    }

    return (
      <StyledLink
        {...rest}
        ref={assignRefs(animationRef, forwardedRef)}
        aria-disabled={isDisabled || rest['aria-disabled']}
        className={className}
        data-link-as-button={rest['data-link-as-button'] || (!asButton && displayAsButton) ? true : undefined}
        data-link-as-link={!rest['data-link-as-button'] && !asButton && !displayAsButton ? true : undefined}
        href={currentHrefToValue}
        iconAnimation={iconAnimation}
        onClick={handleOnClick}
        onMouseEnter={() => setHasHover(true)}
        onMouseLeave={() => setHasHover(false)}
        rel={relProps}
        target={targetProps}
        underline={underline}
        variant={variant}
      >
        {hasIcon && !asButton && !displayAsButton ? (
          <span ref={animationRef}>
            {updatedLabels ? `${updatedLabels[0]} ` : linkLabel}
            <span css={{ whiteSpace: 'nowrap' }}>
              {updatedLabels ? updatedLabels[1] : ''}
              <span
                css={[
                  {
                    label: 'hasIcon',
                    position: 'relative',
                    fontSize: '0.75em',
                    marginLeft: '0.5em',
                    display: 'inline-block',
                    color: 'inherit',
                  },
                  iconAnimation === 'navigational' && {
                    display: 'inline-flex',
                    fontSize: '0.9em',
                    alignItems: 'flex-end',
                    '& span': {
                      height: 'unset',
                    },
                  },
                ]}
              >
                <Icon
                  color={iconColor}
                  css={[
                    iconAnimation === 'jumpLink' && {
                      // !important prevents transform property being overriden by animation
                      // this seems to only be an issue in Storybook, but it feels safer to leave it
                      transform: 'rotate(90deg) !important',
                    },
                    iconAnimation === 'jumpLinkInverse' && {
                      transform: 'rotate(-90deg) !important',
                    },
                    iconAnimation === 'collapse' && {
                      transform: 'rotate(180deg) !important',
                    },
                  ]}
                  // eslint-disable-next-line react/jsx-sort-props
                  className={clsx(
                    ['expand', 'collapse'].includes(iconAnimation) && 'hasCarotIcon',
                    ['navigational', 'jumpLink', 'jumpLinkInverse'].includes(iconAnimation) && 'hasArrowIcon',
                  )}
                  type={iconTypes[iconAnimation]!}
                />
              </span>
            </span>
          </span>
        ) : (
          linkLabel
        )}
      </StyledLink>
    );
  },
);

// manually adding display name or it goes missing on build
LinkComponent.displayName = 'Link';
const Link = decorate(LinkComponent);

export { Link };
export default Link;
